import React, { useState } from 'react';
import SEO from '../components/SEO';
import seoData from '../config/seoData';
import { Phone, MessageCircle, Wrench, Instagram, ArrowLeft, Clock, User, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate, Link } from 'react-router-dom';

const Blog = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const whatsappNumber = "5531971823249";
  const whatsappMessage = encodeURIComponent("Olá! Vi o artigo no site e gostaria de mais informações.");

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
  };

  // Scroll para o topo quando a página carregar
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        {...seoData['/blog']}
        ogImage="https://www.wellttec.com/og-image.jpg"
      />

      {/* Fixed WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-50 animate-bounce-slow">
        <Button
          onClick={handleWhatsAppClick}
          className="bg-green-500 hover:bg-green-600 text-white rounded-full w-16 h-16 shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110"
        >
          <MessageCircle className="w-8 h-8" />
        </Button>
      </div>

      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/equipamentos-disponiveis" className="hover:text-blue-400 transition-colors">Catálogo</Link>
            <Link to="/limpeza-pos-obra" className="hover:text-green-400 transition-colors">Limpeza Pós-Obra</Link>
            <Link to="/locacao-lavadora-piso" className="hover:text-blue-400 transition-colors">Locação</Link>
            <Link to="/blog" className="hover:text-blue-400 transition-colors">Blog</Link>
            <a 
              href="https://www.instagram.com/welltecbrasil/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-blue-400 transition-colors"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button
                onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Início
              </button>
              <button
                onClick={() => { navigate('/servicos'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Serviços
              </button>
              <button
                onClick={() => { navigate('/equipamentos-disponiveis'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Catálogo
              </button>
              <button
                onClick={() => { navigate('/limpeza-pos-obra'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors text-green-400">
                Limpeza Pós-Obra
              </button>
              <button
                onClick={() => { navigate('/locacao-lavadora-piso'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Locação
              </button>
              <button
                onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Blog
              </button>
              <a
                href="https://www.instagram.com/welltecbrasil/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors flex items-center">
                <Instagram className="w-5 h-5 mr-2" />
                Instagram
              </a>
              <Button 
                onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }}
                className="bg-green-500 hover:bg-green-600 w-full mt-2">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Article Header */}
      <div className="bg-slate-900 py-12">
        <div className="container mx-auto px-6">
          <Button 
            onClick={() => navigate('/')} 
            variant="ghost" 
            className="text-white mb-6 hover:text-blue-400"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Guia Completo: Locação de Lavadora de Piso em BH
          </h1>
          <div className="flex items-center space-x-6 text-gray-300">
            <div className="flex items-center">
              <User className="w-4 h-4 mr-2" />
              <span>WellTec</span>
            </div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              <span>Atualizado em 27/03/2025</span>
            </div>
          </div>
        </div>
      </div>

      {/* Artigos Recentes - Limpeza Pós-Obra */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Artigos Sobre Limpeza Pós-Obra
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {/* Artigo 1 */}
            <Card 
              className="hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-gray-100 hover:border-green-500"
              onClick={() => navigate('/blog/custo-limpeza-pos-obra')}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <Clock className="w-4 h-4" />
                  <span>8 min</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Quanto Custa Limpeza Pós-Obra?
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Guia completo de preços 2025 com tabelas e fatores que influenciam
                </p>
                <Button className="bg-green-600 hover:bg-green-700 text-white w-full">
                  Ler Artigo
                </Button>
              </CardContent>
            </Card>

            {/* Artigo 2 */}
            <Card 
              className="hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-gray-100 hover:border-green-500"
              onClick={() => navigate('/blog/limpeza-pos-obra-residencial')}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <Clock className="w-4 h-4" />
                  <span>10 min</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Checklist Limpeza Residencial
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Tudo que deve ser limpo após uma reforma residencial
                </p>
                <Button className="bg-green-600 hover:bg-green-700 text-white w-full">
                  Ler Artigo
                </Button>
              </CardContent>
            </Card>

            {/* Artigo 3 */}
            <Card 
              className="hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-gray-100 hover:border-green-500"
              onClick={() => navigate('/blog/limpeza-galpao-industrial')}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <Clock className="w-4 h-4" />
                  <span>9 min</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Limpeza de Galpão Industrial
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Equipamentos e técnicas para grandes áreas
                </p>
                <Button className="bg-green-600 hover:bg-green-700 text-white w-full">
                  Ler Artigo
                </Button>
              </CardContent>
            </Card>

            {/* Artigo 4 */}
            <Card 
              className="hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-gray-100 hover:border-green-500"
              onClick={() => navigate('/blog/limpeza-vs-convencional')}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <Clock className="w-4 h-4" />
                  <span>7 min</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Pós-Obra vs Convencional
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Entenda as diferenças técnicas e quando contratar
                </p>
                <Button className="bg-green-600 hover:bg-green-700 text-white w-full">
                  Ler Artigo
                </Button>
              </CardContent>
            </Card>

            {/* Artigo 5 */}
            <Card 
              className="hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-gray-100 hover:border-green-500"
              onClick={() => navigate('/blog/remover-cimento-argamassa')}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <Clock className="w-4 h-4" />
                  <span>8 min</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Remover Cimento e Argamassa
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Técnicas profissionais para cada tipo de piso
                </p>
                <Button className="bg-green-600 hover:bg-green-700 text-white w-full">
                  Ler Artigo
                </Button>
              </CardContent>
            </Card>

            {/* Artigo 6 */}
            <Card 
              className="hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-gray-100 hover:border-green-500"
              onClick={() => navigate('/blog/limpeza-condominios')}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <Clock className="w-4 h-4" />
                  <span>8 min</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Limpeza em Condomínios
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Áreas comuns, garagens e salões de festa
                </p>
                <Button className="bg-green-600 hover:bg-green-700 text-white w-full">
                  Ler Artigo
                </Button>
              </CardContent>
            </Card>

            {/* Artigo 7 */}
            <Card 
              className="hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-gray-100 hover:border-green-500"
              onClick={() => navigate('/blog/equipamentos-limpeza-pos-obra')}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <Clock className="w-4 h-4" />
                  <span>9 min</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Equipamentos Profissionais
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Lavadoras, varredeiras e ferramentas especializadas
                </p>
                <Button className="bg-green-600 hover:bg-green-700 text-white w-full">
                  Ler Artigo
                </Button>
              </CardContent>
            </Card>

            {/* Artigo 8 */}
            <Card 
              className="hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-gray-100 hover:border-green-500"
              onClick={() => navigate('/blog/limpeza-comercial')}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <Clock className="w-4 h-4" />
                  <span>8 min</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Limpeza Pós-Obra Comercial
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Prepare lojas e escritórios para inauguração
                </p>
                <Button className="bg-green-600 hover:bg-green-700 text-white w-full">
                  Ler Artigo
                </Button>
              </CardContent>
            </Card>

            {/* Artigo 9 */}
            <Card 
              className="hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-gray-100 hover:border-green-500"
              onClick={() => navigate('/blog/tempo-limpeza-pos-obra')}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <Clock className="w-4 h-4" />
                  <span>7 min</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Quanto Tempo Leva?
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Prazos por tipo de imóvel e área
                </p>
                <Button className="bg-green-600 hover:bg-green-700 text-white w-full">
                  Ler Artigo
                </Button>
              </CardContent>
            </Card>

            {/* Artigo 10 */}
            <Card 
              className="hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-gray-100 hover:border-green-500"
              onClick={() => navigate('/blog/erros-construtoras')}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <Clock className="w-4 h-4" />
                  <span>9 min</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  7 Erros de Construtoras
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Erros comuns ao entregar obras e como evitá-los
                </p>
                <Button className="bg-green-600 hover:bg-green-700 text-white w-full">
                  Ler Artigo
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            
            <img 
              src="https://images.squarespace-cdn.com/content/v1/6944bce717735870c92588a9/6c6006e7-0128-4018-adb3-c63a42fece8d/IMG-20250626-WA0092.jpg?w=1200" 
              alt="Oficina WellTec com lavadoras de piso profissionais"
              className="w-full rounded-lg shadow-xl mb-8"
            />

            <h2 className="text-3xl font-bold text-slate-900 mt-8 mb-4">
              Por que Alugar uma Lavadora de Piso ao Invés de Comprar?
            </h2>
            
            <p className="text-lg text-gray-700 mb-6">
              A <strong>locação de lavadoras de piso em Belo Horizonte</strong> se tornou a escolha inteligente para empresas que buscam eficiência e economia. A WellTec oferece <strong>aluguel de equipamentos de limpeza industrial</strong> com manutenção inclusa e suporte técnico especializado em todo o estado de Minas Gerais.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Vantagens da Locação de Lavadoras de Piso
            </h3>

            <div className="bg-blue-50 p-6 rounded-lg mb-8">
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-3 font-bold">✓</span>
                  <span><strong>Economia inicial:</strong> Não é necessário investimento alto na compra de equipamentos</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-3 font-bold">✓</span>
                  <span><strong>Manutenção inclusa:</strong> Todo o suporte técnico é responsabilidade da locadora</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-3 font-bold">✓</span>
                  <span><strong>Equipamentos atualizados:</strong> Sempre com tecnologia de ponta</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-3 font-bold">✓</span>
                  <span><strong>Flexibilidade:</strong> Alugue apenas pelo período necessário</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-3 font-bold">✓</span>
                  <span><strong>Suporte técnico rápido:</strong> Atendimento ágil em BH e região metropolitana</span>
                </li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Tipos de Lavadoras de Piso Disponíveis para Locação
            </h3>

            <p className="text-lg text-gray-700 mb-6">
              A WellTec oferece diversos modelos de <strong>lavadoras e varredeiras de piso</strong> para atender diferentes necessidades, incluindo <strong>lavadoras pedestres, lavadoras tripuladas a bateria, lavadoras tripuladas a GLP</strong> e <strong>varredeiras industriais</strong>:
            </p>

            <h4 className="text-xl font-bold text-slate-900 mt-6 mb-3">
              1. Lavadoras Pedestres (Walk-Behind)
            </h4>
            <p className="text-gray-700 mb-4">
              Ideais para áreas de <strong>tráfego médio a intenso</strong> em ambientes comerciais e industriais. Modelos como AS510 e SC450 são perfeitos para supermercados, shopping centers e indústrias de médio porte.
            </p>

            <h4 className="text-xl font-bold text-slate-900 mt-6 mb-3">
              2. Lavadoras Tripuladas a Bateria (Ride-On Elétricas)
            </h4>
            <p className="text-gray-700 mb-4">
              Para <strong>grandes áreas industriais e logísticas</strong>. Equipamentos elétricos silenciosos, ideais para ambientes fechados como shoppings, hospitais e galpões. Alto desempenho com operação sustentável e sem emissão de gases.
            </p>

            <h4 className="text-xl font-bold text-slate-900 mt-6 mb-3">
              3. Lavadoras Tripuladas a GLP (Ride-On a Gás)
            </h4>
            <p className="text-gray-700 mb-4">
              Potência e autonomia para <strong>áreas externas e ambientes amplos</strong>. Perfeitas para centros de distribuição, pátios industriais e grandes superfícies que exigem limpeza intensiva e contínua.
            </p>

            <h4 className="text-xl font-bold text-slate-900 mt-6 mb-3">
              4. Varredeiras de Piso
            </h4>
            <p className="text-gray-700 mb-6">
              Essenciais para ambientes que demandam <strong>apenas varredura</strong>, como galpões logísticos, estacionamentos e áreas externas. Também podem ser usadas em conjunto com lavadoras para uma limpeza completa, removendo resíduos sólidos antes da lavagem.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Segmentos que Mais se Beneficiam da Locação
            </h3>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <h4 className="text-lg font-bold text-slate-900 mb-3">🏭 Limpeza Industrial</h4>
                  <p className="text-gray-700">Fábricas, galpões logísticos, armazéns e centros de distribuição</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="text-lg font-bold text-slate-900 mb-3">🏢 Limpeza Comercial</h4>
                  <p className="text-gray-700">Shopping centers, supermercados, hospitais e escritórios</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="text-lg font-bold text-slate-900 mb-3">🏗️ Limpeza Pós-Obra</h4>
                  <p className="text-gray-700">Construtoras e incorporadoras para finalização de obras</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="text-lg font-bold text-slate-900 mb-3">🏘️ Condomínios</h4>
                  <p className="text-gray-700">Garagens, áreas comuns e espaços de lazer</p>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Região de Atendimento: Minas Gerais
            </h3>

            <p className="text-lg text-gray-700 mb-4">
              A WellTec atende <strong>todo o estado de Minas Gerais</strong>, com destaque para:
            </p>

            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li><strong>Região Metropolitana de Belo Horizonte</strong> - BH, Contagem, Betim, Ribeirão das Neves, Sabará, Nova Lima</li>
              <li><strong>Mário Campos</strong> (sede da WellTec)</li>
              <li><strong>Demais regiões de Minas Gerais</strong> - Consulte disponibilidade</li>
            </ul>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Manutenção e Suporte Técnico Inclusos
            </h3>

            <p className="text-lg text-gray-700 mb-6">
              Um dos maiores diferenciais da <strong>locação de equipamentos de limpeza</strong> com a WellTec é o suporte técnico completo:
            </p>

            <div className="bg-gray-50 p-6 rounded-lg mb-8">
              <h4 className="font-bold text-slate-900 mb-3">Serviços Inclusos na Locação:</h4>
              <ul className="space-y-2 text-gray-700">
                <li>• <strong>Manutenção preventiva</strong> periódica</li>
                <li>• <strong>Manutenção corretiva</strong> sem custo adicional</li>
                <li>• <strong>Máquina reserva</strong> em caso de necessidade</li>
                <li>• <strong>Treinamento</strong> para operação segura</li>
                <li>• <strong>Peças originais</strong> garantidas</li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Como Escolher a Lavadora de Piso Ideal?
            </h3>

            <p className="text-lg text-gray-700 mb-4">
              Para escolher o <strong>equipamento de limpeza industrial</strong> ideal, considere:
            </p>

            <ol className="list-decimal list-inside text-gray-700 mb-8 space-y-3">
              <li><strong>Tamanho da área:</strong> Áreas maiores exigem equipamentos automáticos</li>
              <li><strong>Tipo de piso:</strong> Concreto, epóxi, cerâmica - cada um tem suas especificidades</li>
              <li><strong>Nível de sujidade:</strong> Ambientes industriais necessitam de equipamentos mais robustos</li>
              <li><strong>Frequência de uso:</strong> Diária, semanal ou para eventos pontuais</li>
              <li><strong>Tipo de resíduo:</strong> Pó, óleo, lama ou resíduos mistos</li>
            </ol>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Custos e Economia com Locação
            </h3>

            <p className="text-lg text-gray-700 mb-6">
              O <strong>aluguel de lavadora de piso</strong> pode gerar uma economia de até 60% comparado à compra, especialmente quando consideramos:
            </p>

            <ul className="list-disc list-inside text-gray-700 mb-8 space-y-2">
              <li>Investimento inicial zero</li>
              <li>Sem custos com manutenção</li>
              <li>Sem depreciação do equipamento</li>
              <li>Sem necessidade de espaço para armazenamento</li>
              <li>Flexibilidade para trocar de modelo conforme necessidade</li>
            </ul>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Por que Escolher a WellTec?
            </h3>

            <div className="bg-blue-600 text-white p-8 rounded-lg mb-8">
              <h4 className="text-2xl font-bold mb-4">Diferenciais WellTec:</h4>
              <ul className="space-y-3">
                <li>✓ <strong>Experiência comprovada</strong> com grandes marcas</li>
                <li>✓ <strong>Atendimento rápido</strong> em toda Minas Gerais</li>
                <li>✓ <strong>Equipamentos sempre revisados</strong> e em perfeito estado</li>
                <li>✓ <strong>Equipe técnica certificada</strong> e experiente</li>
                <li>✓ <strong>Avaliação 4.4/5.0</strong> no Google</li>
                <li>✓ Localização estratégica em <strong>Mário Campos - MG</strong></li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Solicite um Orçamento Agora
            </h3>

            <p className="text-lg text-gray-700 mb-6">
              Precisa de <strong>locação de lavadora de piso em Belo Horizonte</strong>? Entre em contato com a WellTec e descubra a melhor solução para sua empresa!
            </p>

            <div className="bg-gray-50 p-8 rounded-lg text-center mb-8">
              <h4 className="text-2xl font-bold text-slate-900 mb-4">Entre em Contato</h4>
              <p className="text-lg text-gray-700 mb-6">
                📍 Rua João de Carvalho Silva, 274 - São Tarcísio, Mário Campos - MG<br />
                📞 <a href="tel:+5531971823249" className="text-blue-600 font-bold">(31) 97182-3249</a><br />
                ⏰ Segunda a Sexta, 08:00 às 18:00
              </p>
              <Button 
                onClick={handleWhatsAppClick}
                size="lg"
                className="bg-green-500 hover:bg-green-600 text-white text-lg px-8 py-6"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Solicitar Orçamento via WhatsApp
              </Button>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Perguntas Frequentes
            </h3>

            <div className="space-y-6 mb-8">
              <div>
                <h4 className="text-lg font-bold text-slate-900 mb-2">
                  Qual o prazo mínimo de locação?
                </h4>
                <p className="text-gray-700">
                  A WellTec oferece flexibilidade nos prazos. Desde locações diárias até contratos mensais e anuais, adequando-se à sua necessidade.
                </p>
              </div>

              <div>
                <h4 className="text-lg font-bold text-slate-900 mb-2">
                  A manutenção está incluída no valor da locação?
                </h4>
                <p className="text-gray-700">
                  Sim! Toda manutenção preventiva e corretiva está incluída, garantindo que o equipamento esteja sempre em perfeito funcionamento.
                </p>
              </div>

              <div>
                <h4 className="text-lg font-bold text-slate-900 mb-2">
                  Vocês fazem entrega dos equipamentos?
                </h4>
                <p className="text-gray-700">
                  Sim, realizamos entrega e recolhimento dos equipamentos em toda Minas Gerais.
                </p>
              </div>

              <div>
                <h4 className="text-lg font-bold text-slate-900 mb-2">
                  E se o equipamento apresentar defeito durante a locação?
                </h4>
                <p className="text-gray-700">
                  Nossa equipe técnica realiza o reparo rapidamente, e se necessário, disponibilizamos uma máquina reserva para não interromper suas operações.
                </p>
              </div>
            </div>

            <div className="border-t pt-8 mt-12">
              <p className="text-sm text-gray-600 italic">
                <strong>Palavras-chave relacionadas:</strong> locação lavadora de piso BH, aluguel lavadora industrial Belo Horizonte, equipamento limpeza industrial, locação varredeira piso, manutenção lavadora piso, aluguel equipamento limpeza Contagem, lavadora piso Betim, locação máquina limpeza Mário Campos, limpeza industrial BH, equipamentos limpeza profissional
              </p>
            </div>

          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
                  alt="WellTec Logo" 
                  className="h-20 w-auto"
                />
              </div>
              <p className="text-gray-400 mb-4">
                Especialistas em locação e manutenção de lavadoras de piso industriais.
              </p>
              <a 
                href="https://www.instagram.com/welltecbrasil/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
              >
                <Instagram className="w-5 h-5" />
                <span>@welltecbrasil</span>
              </a>
            </div>
            <div>
              <h5 className="text-lg font-bold mb-4">Links Rápidos</h5>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Início</Link></li>
                <li><Link to="/servicos" className="text-gray-400 hover:text-white transition-colors">Serviços</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors">Blog</Link></li>
                <li><a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Instagram</a></li>
              </ul>
            </div>
            <div>
              <h5 className="text-lg font-bold mb-4">Contato</h5>
              <ul className="space-y-2 text-gray-400">
                <li>(31) 97182-3249</li>
                <li>Mário Campos, MG</li>
                <li>Seg-Sex: 08:00-18:00</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 WellTec - Locações e Serviços Técnicos. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Blog;
